import { getGoogleFontsMetadata } from './fonts.macro' with { type: 'macro' };

const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2';

export const googleFonts = await getGoogleFontsMetadata();

export const fontsByFamily = new Map(googleFonts.map(f => [f.family, f]));

export const googleFontFamilies = googleFonts.map(f => f.family);

export const standardFonts = ['Helvetica', 'Courier', 'Times'];

export type AvailableFont = {
  family: string;
  type: 'standard' | 'google';
  hasItalic: boolean;
  hasBold: boolean;
  hasBoldItalic: boolean;
};

export const availableFonts: AvailableFont[] = [
  ...standardFonts.map(f => ({
    family: f,
    type: 'standard' as const,
    hasItalic: true,
    hasBold: true,
    hasBoldItalic: true,
  })),
  ...googleFonts.map(f => ({
    family: f.family,
    type: 'google' as const,
    hasItalic: f.hasItalic,
    hasBold: f.hasBold,
    hasBoldItalic: f.hasBoldItalic,
  })),
];

export const availableFontsByFamily = new Map(
  availableFonts.map(f => [f.family, f]),
);

export function getAvailableFont(family: string): AvailableFont {
  const font = availableFontsByFamily.get(family);
  if (!font) throw new Error(`Font ${family} not found`);
  return font;
}

const loadedPreviewFonts = new Set<string>();

export function hasLoadedPreviewFont(family: string) {
  return loadedPreviewFonts.has(family);
}

export function loadPreviewFont(family: string) {
  if (loadedPreviewFonts.has(family)) return;

  loadedPreviewFonts.add(family);

  family = encodeURIComponent(family);

  const url = `${GOOGLE_FONTS_URL}?family=${family}&text=${family}`;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

const loadedFonts = new Set<string>();

export function loadFont(family: string) {
  if (loadedFonts.has(family)) return;

  loadedFonts.add(family);

  const url = `${GOOGLE_FONTS_URL}?family=${family}`;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  document.head.appendChild(link);
}

// Used for PDF generation
const ttfFonts = new Map<string, Uint8Array>();

function getVariant(italic: boolean, bold: boolean) {
  if (italic && bold) return '700italic';
  if (italic) return 'italic';
  if (bold) return '700';
  return 'regular';
}

export async function loadTtfFont(
  family: string,
  italic: boolean,
  bold: boolean,
): Promise<Uint8Array> {
  let variant = getVariant(italic, bold);
  const key = `${family}-${variant}`;
  if (ttfFonts.has(key)) {
    return ttfFonts.get(key)!;
  }

  const gfont = fontsByFamily.get(family);

  if (!gfont) {
    throw new Error(`Font ${family} not found`);
  }

  if (!gfont.files[variant]) {
    variant = 'regular';
    if (!gfont.files[variant]) {
      throw new Error(`Font ${family} variant ${variant} not found`);
    }
  }

  const response = await fetch(gfont.files[variant]!);
  const arrayBuffer = await response.arrayBuffer();
  const array = new Uint8Array(arrayBuffer);
  ttfFonts.set(key, array);
  return array;
}
